import { ApplicationStatus, InterviewStatus, UserType } from '../data/collections';

type InputType = 'text' | 'select' | 'switch';

interface SelectOption {
	value: string;
	label: string;
}

interface DialogInput {
	type: InputType;
	label: string;
	name: string;
	options?: SelectOption[];
	defaultValue?: string | boolean;
	multiline?: boolean;
	rows?: number;
	placeholder?: string;
	condition?: (data: Record<string, unknown>) => boolean;
}

interface DialogAction {
	label: string;
	value: string;
	color: string;
}

interface DialogEntry {
	title?: string;
	message?: string;
	inputs?: DialogInput[];
	actions?: DialogAction[];
	actionLabel?: string;
}

export const dialogConfig: Record<string, DialogEntry> = {
	confirmAction: {
		title: 'Please Confirm Action',
		message: 'Are you sure you want to proceed? This action cannot be undone.',
	},
	markEligibility: {
		title: 'Mark Eligibility',
		message: 'Did you want to make this applicant eligible for the interview process? If so, make sure everything is in order and click eligible or not eligible below.',
		actions: [
			{ label: 'Ineligible', value: ApplicationStatus.ineligible, color: 'error' },
			{ label: 'Eligible', value: ApplicationStatus.eligible, color: 'success' },
		],
	},
	changeAppStatus: {
		title: 'Change App Status',
		message: 'Did you want to manually change the status of this application? Choose a new status and click update if so.',
		inputs: [
			{
				type: 'select',
				label: 'App Status',
				name: 'status',
				options: Object.keys(ApplicationStatus).map((key) => ({
					value: ApplicationStatus[key as keyof typeof ApplicationStatus],
					label: ApplicationStatus[key as keyof typeof ApplicationStatus],
				})),
				defaultValue: '',
			},
		],
		actionLabel: 'Update Status',
	},
	changeLoginEmail: {
		title: 'New Username Email',
		message: "Changing the username email address will change this user's log in credentials. To change the email address we have on file for communications, update the applicant, not this.",
		inputs: [{ type: 'text', label: 'New Login Email', name: 'newEmail' }],
		actionLabel: 'Update Login Email',
	},
	notificationsUpdate: {
		title: 'Notifications Settings',
		message: 'Changing this will impact your ability to get timely notifications. Urgent notifications will always override these settings.',
		inputs: [
			{ type: 'switch', label: 'Email Notifications', name: 'email' },
			{ type: 'switch', label: 'SMS Notifications', name: 'sms' },
			{ type: 'text', label: 'Display Name', name: 'callMe' },
			{ type: 'switch', label: 'Forward Emails', name: 'forwardingEnabled', condition: (data) => data.userType !== UserType.applicant },
		],
		actionLabel: 'Save',
	},
	requestRecommendation: {
		title: 'Request Recommendation',
		message: 'To have your recommender upload the letter for you, enter their details below and we will send them an email to begin the process. To view the status of this in the future, review your application.',
		inputs: [
			{ type: 'text', label: 'Who is writing the recommendation?', name: 'name' },
			{ type: 'text', label: 'What is their email address?', name: 'email' },
			{ type: 'text', label: 'How do they know you?', name: 'relation' },
			{ type: 'text', label: 'Who should we say this is for?', name: 'fromName' },
		],
		actionLabel: 'Send Request',
	},
	addAward: {
		title: 'Award Details',
		message: 'Enter details for the award you wish to assign.',
		inputs: [
			{
				type: 'select',
				label: 'Award Type',
				name: 'awardName',
				options: [
					{ value: 'One Time Grant', label: 'One Time Grant' },
					{ value: 'Scholarship Installment', label: 'Scholarship Installment' },
				],
				defaultValue: 'One Time Grant',
			},
			{
				type: 'text',
				label: 'Award Amount (USD)',
				name: 'awardAmount',
			},
			{
				type: 'text',
				label: 'Award Message',
				name: 'followUp',
				placeholder: 'Optional — leave blank for the standard council letter/check follow-up.',
			},
		],
		actionLabel: 'Add Award',
	},
	extendDeadline: {
		title: 'Extend Application Deadline',
		message: 'Set a new deadline for this specific application. This only affects this applicant.',
		inputs: [
			{
				type: 'date' as 'text',
				label: 'New Deadline',
				name: 'newDeadline',
			},
		],
		actionLabel: 'Extend Deadline',
	},
	changeInterviewStatus: {
		title: 'Change Interview Status',
		message: 'Select a new status for this interview. This may trigger other actions like creating or closing the video room.',
		inputs: [
			{
				type: 'select',
				label: 'Interview Status',
				name: 'status',
				options: Object.values(InterviewStatus).map((status) => ({
					value: status,
					label: status,
				})),
				defaultValue: '',
			},
		],
		actionLabel: 'Update Status',
	},
	deleteInterviewSlot: {
		title: 'Delete Interview Slot',
		message: 'Are you sure you want to delete this interview slot? This action cannot be undone.',
	},
	contactDialog: {
		title: 'Send a Message',
	},
	mergeApplicantAccountsDialog: {
		title: 'Merge Applicant Accounts',
	},
	adminActionConfirmation: {
		title: 'Confirm Administrative Action',
		message: 'Are you sure you want to proceed with this action?',
		actionLabel: 'Execute',
	},
	customMessage: {
		title: 'Compose a Custom Message',
		inputs: [
			{ type: 'text', name: 'subject', label: 'Email Subject' },
			{ type: 'text', name: 'emailBody', label: 'Email HTML Body', multiline: true, rows: 10 },
			{ type: 'text', name: 'smsBody', label: 'SMS Text Body', multiline: true, rows: 3 },
		],
		actionLabel: 'Send Message',
	},
	templatedMessage: {
		title: 'Complete Message Template',
		actionLabel: 'Send Templated Message',
	},
	purgeUserData: {
		title: 'Purge User Data',
		message: 'Select an applicant to permanently delete all their associated records. If you choose to expel them, their login credentials will also be deleted. This action is irreversible.',
		actionLabel: 'Remove User',
		inputs: [
			{
				name: 'userId',
				label: 'Select Applicant to Purge',
				type: 'select',
				options: [],
			},
			{
				name: 'expel',
				label: 'Remove user from system (deletes login)',
				type: 'switch',
				defaultValue: false,
			},
		],
	},
	wipeCollections: {
		title: 'Wipe Collections',
		message: 'Select the database you wish to wipe. This action deletes all records in the collections and is irreversible.',
		actionLabel: 'Wipe Database',
	},
	autoScheduleInterviews: {
		title: 'Auto-Schedule Interviews',
	},
	manualScheduleInterview: {
		title: 'Manually Schedule an Interview',
	},
	rescheduleInterview: {
		title: 'Reschedule Interview',
	},
	updateRsvpStatus: {
		title: 'Update RSVP Status',
		message: 'Manually set the RSVP status for this interview.',
		inputs: [
			{
				type: 'select',
				label: 'RSVP Status',
				name: 'rsvpStatus',
				options: [
					{ value: 'yes', label: 'Yes' },
					{ value: 'no', label: 'No' },
					{ value: 'unknown', label: 'Unknown' },
				],
				defaultValue: 'unknown',
			},
		],
		actionLabel: 'Update RSVP',
	},
	purgeDeletedApplications: {
		title: 'Purge All Deleted Applications',
		message: 'Permanently remove every application currently marked Deleted, including related records and files. Prefer Dry Run first.',
		actionLabel: 'Execute',
	},
	findUnownedRecords: {
		title: 'Find Unowned Records',
		message: 'Scan for applications missing from every applicant.applications array and for orphaned component records.',
	},
	purgeUnownedRecords: {
		title: 'Purge Unowned Records',
		message: 'Permanently delete unowned applications and orphaned component records. Prefer Dry Run first.',
		actionLabel: 'Execute',
	},
	cleanupOrphanedStorage: {
		title: 'Cleanup Orphaned Storage',
		message: 'Delete Storage objects that are no longer tied to living applications, applicants, or members. Prefer Dry Run first.',
		actionLabel: 'Execute',
	},
	purgeLogs: {
		title: 'Purge Logs',
		message: 'Delete sitelog and/or dblog entries in bulk by age, or remove a single document by ID.',
		actionLabel: 'Purge',
	},
	templateManager: {},
	emailLogs: {},
};
